import React, { useEffect, useMemo, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Star, X, Send, ThumbsUp } from "lucide-react";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../../firebase";

const ReviewModal = ({ appointment, onClose, onSubmitted }) => {
  const [user] = useAuthState(auth);

  const [review, setReview] = useState({
    rating: 0,
    comment: "",
    categories: {
      communication: 0,
      punctuality: 0,
      professionalism: 0,
      expertise: 0,
    },
    wouldRecommend: true,
    isSubmitting: false,
  });

  // Hover UI state for stars
  const [hoverRating, setHoverRating] = useState(0);
  const [hoverCategory, setHoverCategory] = useState("");
  const [hoverCategoryRating, setHoverCategoryRating] = useState(0);

  // Resolve doctor info – use from appointment if present, otherwise fetch
  const [doctorInfo, setDoctorInfo] = useState(
    appointment?.doctorInfo || null
  );

  useEffect(() => {
    let mounted = true;
    const loadDoctor = async () => {
      if (!appointment?.doctorId || appointment?.doctorInfo) return;
      try {
        const snap = await getDoc(doc(db, "doctors", appointment.doctorId));
        if (mounted && snap.exists()) setDoctorInfo(snap.data());
      } catch {
        /* swallow */
      }
    };
    loadDoctor();
    return () => {
      mounted = false;
    };
  }, [appointment]);

  const appointmentDateStr = useMemo(() => {
    if (!appointment?.scheduledDate) return "";
    try {
      const d = new Date(appointment.scheduledDate);
      const date = d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      if (!appointment?.scheduledTime) return date;
      const t = new Date(`2000-01-01T${appointment.scheduledTime}`);
      const time = t.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
      return `${date} at ${time}`;
    } catch {
      return "";
    }
  }, [appointment]);

  const setCategory = (key, value) =>
    setReview((r) => ({ ...r, categories: { ...r.categories, [key]: value } }));

  const renderStarRow = (value, onChange, size = 28, hover = 0, onHover) => (
    <div style={{ display: "flex", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          onClick={() => onChange(n)}
          onMouseEnter={() => onHover && onHover(n)}
          onMouseLeave={() => onHover && onHover(0)}
          style={{
            width: size,
            height: size,
            cursor: "pointer",
            color: n <= (hover || value) ? "#fbbf24" : "#e5e7eb",
            fill: n <= (hover || value) ? "#fbbf24" : "transparent",
            transition: "all 0.15s ease",
          }}
        />
      ))}
    </div>
  );

  const updateDoctorRating = async (doctorId) => {
    // Recalculate aggregates for the doctor from published reviews
    const q = query(
      collection(db, "reviews"),
      where("doctorId", "==", doctorId),
      where("status", "==", "published")
    );
    const snap = await getDocs(q);
    if (snap.empty) return;

    const rows = snap.docs.map((d) => d.data());
    const total = rows.length;
    const avg =
      rows.reduce((s, r) => s + (Number(r.rating) || 0), 0) / Math.max(total, 1);

    const cats = ["communication", "punctuality", "professionalism", "expertise"];
    const categoryAverages = cats.reduce((acc, k) => {
      const sum = rows.reduce((s, r) => s + (Number(r?.categories?.[k]) || 0), 0);
      acc[k] = sum / Math.max(total, 1);
      return acc;
    }, {});

    await updateDoc(doc(db, "doctors", doctorId), {
      rating: avg,
      totalReviews: total,
      categoryRatings: categoryAverages,
      lastReviewUpdate: serverTimestamp(),
    });
  };

  const handleSubmit = async () => {
    if (!user || !appointment) return;
    if (review.rating === 0) {
      alert("Please select an overall rating.");
      return;
    }
    if (!review.comment.trim()) {
      alert("Please write a short comment about your experience.");
      return;
    }

    setReview((r) => ({ ...r, isSubmitting: true }));

    try {
      const payload = {
        // Patient
        patientId: user.uid,
        patientInfo: {
          name: user.displayName || (user.email ? user.email.split("@")[0] : "Patient"),
          email: user.email || "",
        },
        // Doctor
        doctorId: appointment.doctorId,
        doctorInfo: {
          name: doctorInfo?.name || appointment?.doctorInfo?.name || "Doctor",
          specialty:
            doctorInfo?.specialty || appointment?.doctorInfo?.specialty || "General Practice",
        },
        // Appointment
        appointmentId: appointment.id,
        appointmentDate: appointment.scheduledDate || null,
        serviceName: appointment.serviceName || "Consultation",

        // Review content
        rating: review.rating,
        comment: review.comment.trim(),
        categories: review.categories,
        wouldRecommend: !!review.wouldRecommend,

        // Meta
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: "published",
      };

      const ref = await addDoc(collection(db, "reviews"), payload);

      // best-effort update of aggregates
      if (appointment.doctorId) {
        try {
          await updateDoctorRating(appointment.doctorId);
        } catch {
          /* no-op */
        }
      }

      // Let parent update UI immediately if it wants to
      if (onSubmitted) {
        onSubmitted({
          id: ref.id,
          ...payload,
          createdAt: new Date(),
        });
      }

      alert("✅ Review submitted. Thank you for your feedback!");
      onClose?.();
    } catch (e) {
      console.error("Submit review error:", e);
      alert("❌ Failed to submit review. Please try again.");
      setReview((r) => ({ ...r, isSubmitting: false }));
    }
  };

  if (!appointment) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
      role="dialog"
      aria-modal="true"
    >
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div
        style={{
          backgroundColor: "white",
          borderRadius: 24,
          maxWidth: 600,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 32px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f8fafc",
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#111827",
                margin: "0 0 4px 0",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Star style={{ width: 20, height: 20, color: "#f59e0b" }} />
              Write Review
            </h2>
            <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
              Dr. {doctorInfo?.name || appointment?.doctorInfo?.name || "Doctor"} •{" "}
              {doctorInfo?.specialty ||
                appointment?.doctorInfo?.specialty ||
                "General Practice"}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
              color: "#6b7280",
              padding: 4,
            }}
          >
            <X style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 32 }}>
          {/* Appointment summary */}
          <div
            style={{
              backgroundColor: "#f0f9ff",
              padding: 16,
              borderRadius: 12,
              border: "1px solid #bae6fd",
              marginBottom: 24,
            }}
          >
            <h3
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0369a1",
                margin: "0 0 8px 0",
              }}
            >
              Appointment Details
            </h3>
            <div style={{ fontSize: 13, color: "#0369a1" }}>
              <p style={{ margin: "0 0 4px 0" }}>
                <strong>Service:</strong> {appointment?.serviceName || "Consultation"}
              </p>
              {appointmentDateStr && (
                <p style={{ margin: 0 }}>
                  <strong>Date:</strong> {appointmentDateStr}
                </p>
              )}
            </div>
          </div>

          {/* Overall rating */}
          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Overall Rating *
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginBottom: 8,
              }}
            >
              {renderStarRow(
                review.rating,
                (v) => setReview((r) => ({ ...r, rating: v })),
                32,
                hoverRating,
                setHoverRating
              )}
              <span style={{ fontSize: 18, fontWeight: 600, color: "#374151" }}>
                {hoverRating || review.rating}/5
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              Rate your overall experience with this doctor
            </p>
          </div>

          {/* Category ratings */}
          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Detailed Ratings
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
              {[
                {
                  key: "communication",
                  label: "Communication",
                  desc: "How well did the doctor communicate?",
                },
                {
                  key: "punctuality",
                  label: "Punctuality",
                  desc: "Was the appointment on time?",
                },
                {
                  key: "professionalism",
                  label: "Professionalism",
                  desc: "Professional behavior and manner",
                },
                {
                  key: "expertise",
                  label: "Medical Expertise",
                  desc: "Knowledge and competence shown",
                },
              ].map((c) => (
                <div
                  key={c.key}
                  style={{
                    padding: 16,
                    backgroundColor: "#f9fafb",
                    borderRadius: 8,
                    border: "1px solid #f3f4f6",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#111827",
                          margin: "0 0 2px 0",
                        }}
                      >
                        {c.label}
                      </p>
                      <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                        {c.desc}
                      </p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {renderStarRow(
                        review.categories[c.key],
                        (v) => setCategory(c.key, v),
                        20,
                        hoverCategory === c.key ? hoverCategoryRating : 0,
                        (v) => {
                          setHoverCategory(c.key);
                          setHoverCategoryRating(v);
                        }
                      )}
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#6b7280",
                          minWidth: 20,
                        }}
                      >
                        {(hoverCategory === c.key ? hoverCategoryRating : 0) ||
                          review.categories[c.key] ||
                          "0"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Your Review *
            </label>
            <textarea
              value={review.comment}
              onChange={(e) =>
                setReview((r) => ({ ...r, comment: e.target.value }))
              }
              maxLength={1000}
              placeholder="Share your experience with this doctor. What went well? What could be improved?"
              style={{
                width: "100%",
                padding: 16,
                border: "2px solid #e5e7eb",
                borderRadius: 12,
                fontSize: 14,
                minHeight: 120,
                boxSizing: "border-box",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <p
              style={{
                fontSize: 12,
                color: "#6b7280",
                margin: "8px 0 0 0",
                textAlign: "right",
              }}
            >
              {review.comment.length}/1000 characters
            </p>
          </div>

          {/* Recommend */}
          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                marginBottom: 12,
              }}
            >
              Would you recommend this doctor?
            </label>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setReview((r) => ({ ...r, wouldRecommend: true }))}
                style={{
                  padding: "12px 24px",
                  borderRadius: 8,
                  border: "2px solid",
                  borderColor: review.wouldRecommend ? "#10b981" : "#e5e7eb",
                  backgroundColor: review.wouldRecommend ? "#dcfce7" : "white",
                  color: review.wouldRecommend ? "#166534" : "#6b7280",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  transition: "all 0.2s ease",
                }}
              >
                <ThumbsUp style={{ width: 16, height: 16 }} />
                Yes, I recommend
              </button>
              <button
                onClick={() => setReview((r) => ({ ...r, wouldRecommend: false }))}
                style={{
                  padding: "12px 24px",
                  borderRadius: 8,
                  border: "2px solid",
                  borderColor: !review.wouldRecommend ? "#ef4444" : "#e5e7eb",
                  backgroundColor: !review.wouldRecommend ? "#fee2e2" : "white",
                  color: !review.wouldRecommend ? "#991b1b" : "#6b7280",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  transition: "all 0.2s ease",
                }}
              >
                <X style={{ width: 16, height: 16 }} />
                No, I don't recommend
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "24px 32px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f9fafb",
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
          }}
        >
          <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
            Your review will be visible to other patients
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={onClose}
              disabled={review.isSubmitting}
              style={{
                padding: "12px 20px",
                backgroundColor: "white",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                review.isSubmitting || review.rating === 0 || !review.comment.trim()
              }
              style={{
                padding: "12px 20px",
                background:
                  review.isSubmitting || review.rating === 0 || !review.comment.trim()
                    ? "#e5e7eb"
                    : "linear-gradient(135deg, #f59e0b, #d97706)",
                color:
                  review.isSubmitting || review.rating === 0 || !review.comment.trim()
                    ? "#9ca3af"
                    : "white",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor:
                  review.isSubmitting || review.rating === 0 || !review.comment.trim()
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {review.isSubmitting ? (
                <>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      border: "2px solid transparent",
                      borderTop: "2px solid white",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  Submitting...
                </>
              ) : (
                <>
                  <Send style={{ width: 16, height: 16 }} />
                  Submit Review
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
